import { jsx } from '@emotion/core';
import t from 'react-translate';
import moment from 'moment';
import React from 'react';
import { CompletionStatusEnum, UserEnrollment } from 'redux/schemas/models/org-users';
import NvProgressGauge, { ProgressGaugeSize, ProgressGaugeType } from 'shared/components/nv-progress-gauge';
import NvProgressBar from 'shared/components/nv-progress-bar';
import { warning, primary, success, gray7, gray4 } from 'styles/global_defaults/colors';
import NvPopover from 'shared/components/nv-popover';
import { useSelector } from 'react-redux';
import { getCourseAliases } from 'redux/selectors/course';

const CourseProgress = ({ enrollment }: { enrollment: UserEnrollment }) => {
  const aliases = useSelector((state) => getCourseAliases(state, enrollment?.course?.catalogId));
  const progress = displayBarOrGauge(enrollment, aliases);
  const disabledCompletionStatuses = [CompletionStatusEnum.INCOMPLETE, CompletionStatusEnum.REMOVED];

  const popoverContent = (item) => (
    <span className='text-black bold'>
      {`${item.label} ${t.MENTEES.EARNED_OUT_OF_TOTAL(item.currentValue, item.maxValue)}`}
    </span>
  );

  return (
    <div className='d-flex flex-column justify-content-center w-100 py-2'>
      <div className='d-flex align-item-center w-100'>
        {progress && progress.map((item) => (
          <React.Fragment key={item.label}>
            { item.type === 'bar' ? (
              <div className='w-100 pr-4'>
                <NvPopover
                  showOnHover
                  placement='top'
                  content={popoverContent(item)}
                >
                  <div className='mb-1 mt-2'>
                    <NvProgressBar
                      maxValue={item.maxValue}
                      currentValue={item.currentValue}
                      courseClosed={enrollment.completionProgress === CompletionStatusEnum.INCOMPLETE}
                      activeColor={disabledCompletionStatuses.includes(enrollment.completionProgress)
                        ? gray4
                        : item.activeColor}
                    />
                  </div>
                </NvPopover>
              </div>
            ) : (
              <NvPopover
                showOnHover
                placement='top'
                content={popoverContent(item)}
              >
                <div className='pr-1 pb-1'>
                  <NvProgressGauge
                    maxValue={item.maxValue}
                    currentValue={item.currentValue}
                    activeColor={disabledCompletionStatuses.includes(enrollment.completionProgress)
                      ? gray4
                      : item.activeColor}
                    size={ProgressGaugeSize.SMALL}
                    displayType={ProgressGaugeType.ACTUAL_ONLY}
                    bgColor={gray7}
                  />
                </div>
              </NvPopover>
            )}
          </React.Fragment>
        ))}
      </div>
      <div className='text-small font-weight-bold completion-status'>
        {getCompletionProgressText(enrollment)}
      </div>
      <div className='text-small font-weight-bold text-primary view-points-break-down'>
        {t.INSTITUTIONS.ROLES.VIEW_DETAILS()}
      </div>
    </div>
  );
};

const displayBarOrGauge = (enrollment: UserEnrollment, aliases) => {
  /* Show the progress bar in these conditions:
    * 1. Any points have been awarded
    * 2. Some # of assignments are required and some have been
    * completed
    * 3. Some # of todos are required and some have been completed
    * 4. Automatic completions are disabled, no # of assignemnts
    * or todos are required, and some to-dos have been completed
    * Hide the progress bar in all other cases.
  */

  const { course, progress } = enrollment;
  const progressBars = [];

  if (course.automaticCompletionCriteria) {
    const numberOfCriteria = (course.automaticCompletionCriteria.requiredAssignmentsCount > 0 ? 1 : 0)
      + (course.automaticCompletionCriteria.totalRequiredTodosCount > 0 ? 1 : 0)
      + (course.automaticCompletionCriteria.requiredPoints > 0 ? 1 : 0);

    if (course.automaticCompletionCriteria.requiredPoints > 0) {
      progressBars.push({
        currentValue: progress.pointsReceived,
        maxValue: course.automaticCompletionCriteria.requiredPoints,
        activeColor: success,
        type: numberOfCriteria > 1 ? 'gauge' : 'bar',
        label: t.MENTEES.EARNED_POINTS({ ...aliases.pointsAliases }),
      });
    }

    if (course.automaticCompletionCriteria.requiredAssignmentsCount > 0) {
      progressBars.push({
        currentValue: progress.numRequiredAssignmentsCompleted,
        maxValue: course.automaticCompletionCriteria.requiredAssignmentsCount,
        activeColor: warning,
        type: numberOfCriteria > 1 ? 'gauge' : 'bar',
        label: t.MENTEES.COMPLETED_REQUIRED_ASSIGNMENTS({ ...aliases.assignmentAliases }),
      });
    }
    if (course.automaticCompletionCriteria.totalRequiredTodosCount > 0) {
      progressBars.push({
        currentValue: progress.numRequiredTodosCompleted,
        maxValue: course.automaticCompletionCriteria.totalRequiredTodosCount,
        activeColor: primary,
        type: numberOfCriteria > 1 ? 'gauge' : 'bar',
        label: t.MENTEES.COMPLETED_REQUIRED_TODOS(),
      });
    }
  }

  if (progressBars.length === 0 && progress.totalPoints && progress.pointsReceived) {
    progressBars.push({
      currentValue: progress.pointsReceived,
      maxValue: progress.totalPoints,
      activeColor: success,
      type: 'bar',
      label: t.MENTEES.EARNED_POINTS({ ...aliases.pointsAliases }),
    });
  }
  return progressBars;
};

const getCompletionProgressText = (enrollment: UserEnrollment) => {
  switch (enrollment.completionProgress) {
    case CompletionStatusEnum.ENROLLED:
      return <span className='gray-2'>{t.CUSTOM_DOWNLOADS.DROPDOWN.ENROLLED()}</span>;
    case CompletionStatusEnum.INCOMPLETE:
      return <span className='gray-3'>{t.INSTITUTIONS.LICENSES.INCOMPLETE()}</span>;
    case CompletionStatusEnum.WITHDRAWN:
      return <span className=''>{t.INSTITUTIONS.LICENSES.WITHDRAWN(moment(enrollment.enrollmentStatusUpdatedAt).format('L'))}</span>;
    case CompletionStatusEnum.REMOVED:
      return <span>{t.INSTITUTIONS.LICENSES.REMOVED(moment(enrollment.enrollmentStatusUpdatedAt).format('L'))}</span>;
    case CompletionStatusEnum.IN_PROGRESS:
      return <span className='text-primary'>{t.INSTITUTIONS.LICENSES.IN_PROGRESS()}</span>;
    case CompletionStatusEnum.COMPLETED:
      return <span className='text-success'>{t.INSTITUTIONS.LICENSES.COMPLETED()}</span>;
    default:
      return '';
  }
};
export default CourseProgress;
